import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BlogPage = () => {
  useEffect(() => {
    document.title = 'Roofing Blog | NWA Roofing Tips & Advice | Fayetteville, Rogers, Bentonville';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert roofing advice and tips from NWA Roofing. Learn about storm damage, roof maintenance, insurance claims, and more for Northwest Arkansas homeowners.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'roofing blog, roofing tips, NWA roofing advice, storm damage tips');
    }
  }, []);

  const blogPosts = [
    {
      title: 'How to Identify Hail Damage on Your Roof in Northwest Arkansas',
      excerpt: 'Hail storms are common in NWA and can cause significant roof damage. Learn how to spot hail damage on your asphalt shingles, when to call for a professional inspection, and how to file an insurance claim for storm damage repair.',
      image: 'https://public.readdy.ai/ai/img_res/63033d5f3d371982c823a0aa5f6a6791.jpg',
      date: 'January 15, 2025',
      category: 'Storm Damage',
      slug: 'how-to-identify-hail-damage-on-your-roof'
    },
    {
      title: '5 Signs Your Fayetteville Home Needs a New Roof',
      excerpt: 'Not sure if you need roof replacement or just repairs? Discover the five key warning signs that indicate your Fayetteville home needs a new roof, including age, missing shingles, leaks, and energy efficiency issues.',
      image: 'https://readdy.ai/api/search-image?query=residential%20home%20with%20old%20damaged%20roof%20showing%20multiple%20warning%20signs%20including%20missing%20shingles%20and%20wear%2C%20comparison%20with%20new%20roof%20installation%20in%20Northwest%20Arkansas%20neighborhood&width=800&height=500&seq=blog-new-roof-signs&orientation=landscape',
      date: 'January 10, 2025',
      category: 'Roof Replacement',
      slug: 'signs-your-fayetteville-home-needs-new-roof'
    },
    {
      title: 'Navigating Insurance Claims for Roof Damage in NWA',
      excerpt: 'Filing an insurance claim for roof damage can be confusing. This comprehensive guide walks you through the entire process, from documenting damage to working with adjusters, ensuring you receive fair compensation for your Northwest Arkansas home.',
      image: 'https://readdy.ai/api/search-image?query=insurance%20adjuster%20and%20homeowner%20reviewing%20roof%20damage%20documentation%20with%20clipboard%20and%20tablet%2C%20professional%20consultation%20showing%20insurance%20claim%20process%20for%20storm%20damaged%20roof&width=800&height=500&seq=blog-insurance-claims&orientation=landscape',
      date: 'January 5, 2025',
      category: 'Insurance Claims',
      slug: 'navigating-insurance-claims-for-roof-damage'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section - Mobile Optimized */}
      <header className="relative min-h-[40vh] sm:min-h-[50vh] lg:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center py-12 sm:py-0">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-4">
            Roofing Tips & Expert Advice
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white max-w-3xl mx-auto leading-relaxed px-4">
            Stay informed with the latest roofing insights, maintenance tips, and industry news from Northwest Arkansas experts
          </p>
        </div>
      </header>

      {/* Blog Grid - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {blogPosts.map((post, index) => (
              <article
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group touch-manipulation"
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.alt}
                      title={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 flex-wrap gap-2">
                      <time dateTime={post.date}>{post.date}</time>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 group-hover:text-blue-600 transition-colors leading-tight">
                      {post.title}
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                      {post.excerpt}
                    </p>
                    <span className="text-blue-600 font-semibold flex items-center group-hover:text-blue-700 text-sm sm:text-base">
                      Read More <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Need Professional Roofing Help?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed px-4">
            Get your <strong>free inspection</strong> and connect with Northwest Arkansas's most trusted roofing contractors today.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-lg mx-auto">
            <a
              href="tel:4793455312"
              className="bg-blue-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-blue-700 transition-all shadow-xl flex items-center justify-center whitespace-nowrap touch-manipulation active:scale-95"
              aria-label="Call for roofing help"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call (479) 345-5312
            </a>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl flex items-center justify-center whitespace-nowrap touch-manipulation active:scale-95"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;